/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Button, Typography, Modal, Box } from '@mui/material';
import { Section } from '../../components/Section';
import { v4 } from 'uuid';
import { CustomDateRangePicker } from '../../components/CustomDateRangePicker';
import useErrorHandler from '../../utils/userErrorHandler';
import { Link } from 'react-router-dom';
import useCampaignsQuery from './gql/campaigns';
import useDeleteCampaignMutation from './gql/deleteCampaign';

const AbsencesPage: React.FC = () => {
    const [getCampaigns, campaignsResp] = useCampaignsQuery();
    const [deleteCampaign, deletedCampaignResp] = useDeleteCampaignMutation();
    useErrorHandler(campaignsResp.error, deletedCampaignResp.error);

    const [selected, setSelected] = useState<number>();
    const [modalOpen, setModalOpen] = useState(false);
    const [date, setDate] = useState<
        {
            startDate: Date;
            endDate: Date;
            key: string;
        }[]
    >([
        {
            startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1),
            endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
            key: 'selection',
        },
    ]);

    useEffect(() => {
        if (selected) handleDataFetch();
    }, [deletedCampaignResp.data]);

    const handleDateChange = (item: { selection: { startDate: Date; endDate: Date; key: string } }) => {
        setDate([item.selection]);
    };

    const handleModal = (id: number) => {
        setModalOpen(true);
        setSelected(id);
    };

    const handleDelete = async () => {
        await deleteCampaign({ id: selected });
        setModalOpen(false);
    };

    const handleDataFetch = async () => {
        await getCampaigns({ from: date[0].startDate, to: date[0].endDate });
    };

    return (
        <Paper css={containerStyles} elevation={0}>
            <Section title={`Kampányok`}>
                <div css={sectionStyles}>
                    <CustomDateRangePicker ranges={date} onChange={handleDateChange} />
                    <div css={someInputContainerStyles}>
                        <div css={{ minWidth: 226 }} />
                        <div css={buttonConatiner}>
                            <Button size="large" variant="contained" onClick={handleDataFetch}>
                                Lekérés
                            </Button>
                        </div>
                    </div>
                    <Link to="/marketing/campaign/create" css={buttonContainerStyle}>
                        <Button variant="contained" color="success">
                            Új kampány létrehozása
                        </Button>
                    </Link>
                    {campaignsResp.data?.length !== undefined && campaignsResp.data?.length !== 0 ? (
                        <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">Id</TableCell>
                                        <TableCell align="center">Név</TableCell>
                                        <TableCell align="center">Kezdete</TableCell>
                                        <TableCell align="center">Vége</TableCell>
                                        <TableCell align="center">Műveletek</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaignsResp.data?.map((row) => (
                                        <TableRow key={v4()} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell align="center">{row.id}</TableCell>
                                            <TableCell align="center">{row.name}</TableCell>
                                            <TableCell align="center">{row.start}</TableCell>
                                            <TableCell align="center">{row.end}</TableCell>
                                            <TableCell align="center" css={actionsColStyle}>
                                                <Button variant="contained" color="error" onClick={() => handleModal(row.id)}>
                                                    Törlés
                                                </Button>
                                                <Link to={`/marketing/campaign/${row.id}/edit`} style={{ textDecoration: 'none' }}>
                                                    <Button variant="contained" color="success">
                                                        Szerkesztés
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : campaignsResp.data?.length === 0 ? (
                        <Typography variant="body1" color="primary" align="center">
                            Nincs megjeleníthető adat a megadott feltételek alapján!
                        </Typography>
                    ) : null}
                </div>
            </Section>
            <Modal open={modalOpen} onClose={() => setModalOpen(false)} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
                <Box css={boxStyles}>
                    <Typography id="delete-modal-title" variant="h6" component="h2" style={{ marginBottom: '1rem' }}>
                        Biztosan törölni szeretnéd a kampányt?
                    </Typography>
                    <div css={buttonContainerStyles}>
                        <Button size="large" variant="contained" color="error" onClick={handleDelete}>
                            Igen
                        </Button>
                        <Button size="large" variant="contained" onClick={() => setModalOpen(false)}>
                            Nem
                        </Button>
                    </div>
                </Box>
            </Modal>
        </Paper>
    );
};

const containerStyles = css`
    max-width: 1500px;
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    margin: auto;
    margin-bottom: 25px;
    margin-top: 25px;
    gap: 25px;
`;

const sectionStyles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    & > * + * {
        margin-top: 20px !important;
    }
`;

const someInputContainerStyles = css`
    width: 931px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 1000px) {
        flex-direction: column;
        & > * + * {
            margin-top: 20px !important;
        }
    }
`;

const buttonConatiner = css`
    min-width: 352px;
    display: flex;
    justify-content: center;
`;

const buttonContainerStyle = css`
    text-decoration: none;
    margin-left: auto;
    margin-top: 20px;
    margin-bottom: 20px;
`;

const actionsColStyle = css`
    & > * + * {
        margin-left: 20px !important;
    }
`;

const boxStyles = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    border: 2px solid #0288d1;
    boxshadow: 24;
    padding: 1rem;
`;

const buttonContainerStyles = css`
    display: flex;
    justify-content: space-evenly;
    margin-top: 1rem;
`;

export default AbsencesPage;
